import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { estimateKeys } from '@/lib/queryKeys';
import type { Estimate, PdfDocument } from '@/types/estimate';
import type { PublicWisetackCta } from '@/types/public';

interface EstimateDetailResponse {
  estimate: Estimate;
  staleItems: unknown[];
  activePdf: PdfDocument | null;
  wisetack?: PublicWisetackCta | null;
  depositCollectedSummary?: Estimate['depositCollectedSummary'];
}

/**
 * Fetches a single estimate by ID from the operations microservice.
 * Returns the estimate with its response-root siblings merged in: `activePdf`,
 * `wisetack`, and `depositCollectedSummary` (what a collected deposit actually
 * charged — the detail page's fee line and the internal PDF's fee note both
 * read it, so dropping it here silently renders the net as the whole story).
 */
export function useEstimate(id: string) {
  return useApiQuery<Estimate>({
    queryKey: estimateKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<EstimateDetailResponse>(
        operationsClient,
        opsEstimateEndpoints.detail(id),
      );
      return {
        ...res.estimate,
        activePdf: res.activePdf,
        wisetack: res.wisetack ?? null,
        depositCollectedSummary: res.depositCollectedSummary ?? null,
      };
    },
    enabled: Boolean(id),
  });
}
